// Site-wide JS init. The heavy lifting (dropdowns, collapse, accordions,
// tooltips) is stock Bootstrap 4 + jQuery, vendored under /js/vendor/ and
// loaded via plain script tags in the layout. This bundle (esbuild, deferred)
// only runs the small init the OU pages previously did inline: activate
// Bootstrap tooltips. (WOW.js/animate.css were dropped — no element on any
// page carries their classes; the old Adobe bundle added them at runtime.)
import "$styles/index.css"

// --- Publication record: faceted browse (/products/publications) ------------
// Progressive enhancement ONLY. Every row is in the HTML at build time, and the
// interactive layer (search box, histogram, facet rail) ships `hidden` — so with
// this script absent you get the complete list and no dead controls.
//
// Filter state mirrors into the URL hash, so a filtered view is a shareable
// link: #year=2025&group=Publication&author=Joe%20Ripberger&q=tornado
//
// TWO POOLS (owner decision 2026-10-06). `rows` are the rendered list items —
// only entries with a publicly available link. `record` is rows PLUS the
// unlisted entries from the #pub-unlisted JSON block, and is what the rail's
// stats count: like the year chart (static, built from all entries), they
// describe the whole record, while the list and the Type facet counts
// describe what is listed. (A by-month chart was removed 2026-10-06.)

function initPublicationFilters() {
  const page = document.querySelector(".pub-shell")
  if (!page) return

  const items = Array.from(page.querySelectorAll(".pub-item"))
  const groupsEls = Array.from(page.querySelectorAll(".pub-year-group"))
  const search = document.getElementById("pub-search")
  const rail = document.getElementById("pub-rail")
  const chart = document.getElementById("pub-chart")
  const count = document.getElementById("pub-count")
  const empty = document.getElementById("pub-empty")
  const yearButtons = Array.from(page.querySelectorAll("#pub-bars-year .pub-bar"))
  const boxes = Array.from(page.querySelectorAll('.pub-facet input[type="checkbox"]'))
  // Stats in the callout rail that track the active filters. Absent ids are
  // simply skipped, so the rail can carry static figures alongside these.
  const stats = {
    total: document.getElementById("stat-total"),
    pub: document.getElementById("stat-pub"),
    pres: document.getElementById("stat-pres"),
    data: document.getElementById("stat-data"),
    linked: document.getElementById("stat-linked"),
    authors: document.getElementById("stat-authors"),
    scope: document.getElementById("stat-scope")
  }

  // Reveal the interactive layer now that it can actually work.
  ;[search, rail, chart].forEach((el) => { if (el) el.hidden = false })
  // The rail is a <details>; open by default on wide screens, collapsed on
  // phones where it would otherwise bury the results.
  if (rail) rail.open = window.matchMedia("(min-width: 768px)").matches

  const state = { q: "", year: null, group: new Set(), author: new Set() }

  const authorsOf = (el) => (el.dataset.authors ? el.dataset.authors.split("|") : [])

  // Every entry as a plain record with the same string fields, whether it came
  // from a rendered row or from the unlisted JSON — so one matches() serves both.
  const rows = items.map((el) => ({
    el,
    year: el.dataset.year,
    group: el.dataset.group,
    authors: authorsOf(el),
    search: el.dataset.search,
    linked: Boolean(el.querySelector(".pub-item-title a"))
  }))
  let unlisted = []
  try {
    const blob = document.getElementById("pub-unlisted")
    if (blob) unlisted = JSON.parse(blob.textContent)
  } catch (_) {
    // A broken blob must not take the list down with it; the charts and stats
    // just fall back to counting the listed rows.
  }
  const record = rows.concat(unlisted)

  // A record matches when it satisfies every active facet. `skip` lets the count
  // pass ask "what WOULD this facet's options yield" while ignoring itself —
  // otherwise selecting one author would empty every other author's option.
  function matches(r, skip) {
    if (skip !== "q" && state.q && !r.search.includes(state.q)) return false
    if (skip !== "year" && state.year && r.year !== state.year) return false
    if (skip !== "group" && state.group.size && !state.group.has(r.group)) return false
    if (skip !== "author" && state.author.size &&
        !r.authors.some((a) => state.author.has(a))) return false
    return true
  }

  // Counts against the LISTED rows: a facet option answers "what would the list
  // show". Only Type prints its number; author names carry no count (owner
  // request) but are still dimmed when nothing listed is left under them.
  function updateFacetCounts() {
    const groupPool = rows.filter((r) => matches(r, "group"))
    const authorPool = rows.filter((r) => matches(r, "author"))
    page.querySelectorAll("[data-facet-option]").forEach((li) => {
      const [facet, ...rest] = li.dataset.facetOption.split(":")
      const value = rest.join(":")
      const n = facet === "group"
        ? groupPool.filter((r) => r.group === value).length
        : authorPool.filter((r) => r.authors.includes(value)).length
      const badge = li.querySelector("[data-count-for]")
      if (badge) badge.textContent = n
      const box = li.querySelector("input")
      // Keep a checked option usable even at zero, so it can be unchecked.
      li.classList.toggle("is-empty", n === 0 && !(box && box.checked))
    })
  }

  function apply() {
    let shown = 0
    rows.forEach((r) => {
      const ok = matches(r, null)
      r.el.hidden = !ok
      if (ok) shown += 1
    })

    // Hide a year heading once nothing under it survives, and keep its count honest.
    groupsEls.forEach((g) => {
      const visible = Array.from(g.querySelectorAll(".pub-item")).filter((el) => !el.hidden).length
      g.hidden = visible === 0
      const badge = g.querySelector("[data-year-count]")
      if (badge) badge.textContent = visible + (visible === 1 ? " entry" : " entries")
    })

    count.textContent = shown === rows.length
      ? `${shown} entries`
      : `${shown} of ${rows.length} entries`
    if (empty) empty.hidden = shown !== 0

    // The rail's figures are AGGREGATES — the whole record under the active
    // filters, listed or not — matching the charts rather than the list.
    const live = record.filter((r) => matches(r, null))
    const set = (el, n) => { if (el) el.textContent = n }
    set(stats.total, live.length)
    set(stats.pub, live.filter((r) => r.group === "Publication").length)
    set(stats.pres, live.filter((r) => r.group === "Presentation").length)
    set(stats.data, live.filter((r) => r.group === "Published Database").length)
    set(stats.linked, live.filter((r) => r.linked).length)
    // Distinct people across the matching entries, not the whole record —
    // otherwise "13 entries / 13 authors credited" reads as a claim about those 13.
    const people = new Set()
    live.forEach((r) => r.authors.forEach((a) => { if (a) people.add(a) }))
    set(stats.authors, people.size)

    // Say plainly what the figures above are counting. Without this the numbers
    // move when a filter changes and nothing on screen explains the new scope.
    if (stats.scope) {
      const parts = []
      if (state.year) {
        parts.push(state.year)
      } else {
        parts.push("All years")
      }
      if (state.group.size) parts.push(Array.from(state.group).join(" + "))
      if (state.author.size) parts.push(Array.from(state.author).join(" + "))
      if (state.q) parts.push(`matching \u201c${state.q}\u201d`)
      stats.scope.textContent = parts.join(" \u00b7 ")
    }

    yearButtons.forEach((b) => b.setAttribute("aria-pressed", String(b.dataset.year === state.year)))
    updateFacetCounts()
    writeHash()
  }

  function writeHash() {
    const p = new URLSearchParams()
    if (state.q) p.set("q", state.q)
    if (state.year) p.set("year", state.year)
    state.group.forEach((v) => p.append("group", v))
    state.author.forEach((v) => p.append("author", v))
    const next = p.toString()
    // replaceState, not a hash assignment: filtering must not stack up history
    // entries for the back button to walk through.
    window.history.replaceState(null, "", next ? "#" + next : window.location.pathname)
  }

  function readHash() {
    state.q = ""; state.year = null
    state.group.clear(); state.author.clear()
    const p = new URLSearchParams(window.location.hash.replace(/^#/, ""))
    state.q = (p.get("q") || "").trim().toLowerCase()
    // Only accept a year the page actually has, so a stale link can't show an
    // empty list with a year selected that doesn't exist.
    const y = p.get("year")
    if (y && yearButtons.some((b) => b.dataset.year === y)) state.year = y
    p.getAll("group").forEach((v) => state.group.add(v))
    p.getAll("author").forEach((v) => state.author.add(v))

    if (search) search.value = state.q
    boxes.forEach((b) => {
      b.checked = b.name === "group" ? state.group.has(b.value) : state.author.has(b.value)
    })
  }

  function reset() {
    state.q = ""; state.year = null
    state.group.clear(); state.author.clear()
    if (search) search.value = ""
    boxes.forEach((b) => { b.checked = false })
    apply()
  }

  if (search) {
    search.addEventListener("input", () => {
      state.q = search.value.trim().toLowerCase()
      apply()
    })
  }

  boxes.forEach((b) => {
    b.addEventListener("change", () => {
      const set = b.name === "group" ? state.group : state.author
      if (b.checked) set.add(b.value); else set.delete(b.value)
      apply()
    })
  })

  yearButtons.forEach((b) => {
    b.addEventListener("click", () => {
      // Clicking the selected year clears it — the bar is a toggle.
      state.year = state.year === b.dataset.year ? null : b.dataset.year
      apply()
    })
  })

  const resetBtn = document.getElementById("pub-reset")
  if (resetBtn) resetBtn.addEventListener("click", reset)
  const resetInline = document.getElementById("pub-reset-inline")
  if (resetInline) resetInline.addEventListener("click", reset)

  window.addEventListener("hashchange", () => { readHash(); apply() })

  readHash()
  apply()
}

// Deliberately its own listener: the handler below bails early when jQuery is
// absent, and the publication filters use no jQuery at all — they must not be
// collateral damage if the vendored bundle fails to load.
document.addEventListener("DOMContentLoaded", initPublicationFilters)

document.addEventListener("DOMContentLoaded", () => {
  const $ = window.jQuery
  if (!$) return
  $('[data-toggle="tooltip"]').tooltip()

  // --- Mobile nav drawer ----------------------------------------------------
  // The OU theme (Material Kit lineage) builds the mobile menu as an off-canvas
  // drawer: .navbar-collapse is permanently display:block, parked off-screen at
  // translate3d(300px,0,0), and slides in only when an ANCESTOR carries
  // .nav-open (base.min.css line ~4572). Bootstrap's collapse plugin only
  // toggles .show — which no rule in the theme reacts to — so the hamburger
  // toggled a class that moved nothing and the menu never opened.
  //
  // The original CMS pages got .nav-open from OU's own AEM clientlib, which the
  // static capture never included: DEV/site (the golden reference) is broken the
  // same way, so this is a missing piece, not a port regression. Mirror
  // Bootstrap's collapse state onto <body> and the theme's CSS does the rest.
  const $menu = $("#ippra-navbar-menu")
  const $toggler = $(".navbar-toggler")
  if ($menu.length) {
    $menu.on("show.bs.collapse", () => document.body.classList.add("nav-open"))
    $menu.on("hide.bs.collapse", () => document.body.classList.remove("nav-open"))

    // Escape closes and returns focus to the hamburger — the keyboard
    // expectation for anything that behaves like a dialog.
    document.addEventListener("keydown", (e) => {
      if (e.key === "Escape" && document.body.classList.contains("nav-open")) {
        $menu.collapse("hide")
        $toggler.trigger("focus")
      }
    })

    // A tap on a real link navigates, so close the drawer behind it (bfcache
    // would otherwise restore the page with the menu still open). Dropdown
    // toggles are excluded: they open submenus INSIDE the drawer.
    $menu.on("click", "a:not([data-toggle])", () => $menu.collapse("hide"))
  }
  // rail video modal: load the iframe only while the modal is open, so
  // the video neither loads up front nor keeps playing after close
  $("#aside-video-modal")
    .on("show.bs.modal", function () {
      const f = this.querySelector("iframe")
      if (f && !f.src) f.src = f.dataset.src
    })
    .on("hidden.bs.modal", function () {
      const f = this.querySelector("iframe")
      if (f) f.removeAttribute("src")
    })
})
